'use client';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { env } from '@repo/env';
import { CtaButton } from '@repo/ui/CtaButton';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { theme } from '../NerdWalletThemeProvider';
import { SlideUp } from './SlideUp';
import { CPC_OFFERS_DIALOG_CONTENT } from './config';
import type { CPCButtonAnalytics, CPCOfferProps } from './types';

const iconBoxStyles = {
  width: {
    xs: 36,
    sm: 52,
  },
  height: {
    xs: 36,
    sm: 52,
  },
  padding: {
    xs: '1.5rem',
    sm: '2rem',
  },
};

type CPCDialogProps = CPCOfferProps & {
  buttonAnalytics: CPCButtonAnalytics;
  open: boolean;
  onClose: () => void;
};

export function CPCDialog({
  buttonAnalytics,
  cta,
  analyticsPayload,
  displayName,
  image,
  open,
  onClose,
}: CPCDialogProps) {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const TransitionComponent = useMemo(() => (isMobile ? SlideUp : Fade), [isMobile]);
  const [contentReady, setContentReady] = useState(false);

  // Defer heavy content until after first paint to improve INP (shell paints first).
  useEffect(() => {
    if (!open) {
      setContentReady(false);
      return;
    }
    let id1: number | undefined;
    const id2 = requestAnimationFrame(() => {
      id1 = requestAnimationFrame(() => {
        setContentReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(id2);
      if (id1 != null) cancelAnimationFrame(id1);
    };
  }, [open]);

  const offerId = analyticsPayload.offerId;

  if (!offerId || !cta) {
    return null;
  }

  const config = CPC_OFFERS_DIALOG_CONTENT[offerId];
  if (!config) {
    return null;
  }

  const { apyDescriptor, definitiveArticle, monthlyFeeDescriptor, content, extraContent } = config;

  return (
    <Dialog
      fullWidth={!isMobile}
      maxWidth={false}
      keepMounted
      onClose={onClose}
      open={open}
      slots={{ transition: TransitionComponent }}
      slotProps={{
        paper: {
          sx: isMobile
            ? {
                // bottom-sheet / drawer look
                m: 0,
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                maxWidth: '100%',
                maxHeight: '93vh',
                '@supports (height: 100dvh)': { maxHeight: '93dvh' },
                overflow: 'hidden',
                borderRadius: '1rem 1rem 0 0',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '2rem',
                gap: '1rem',
              }
            : {
                // regular centered modal look
                boxShadow:
                  '0 8px 16px 0 rgba(100, 102, 106, 0.33), 0 4px 8px 0 rgba(100, 104, 106, 0.15)',
                width: '100%',
                maxHeight: '94vh',
                maxWidth: 538,
                '@supports (height: 100dvh)': { maxHeight: '94dvh' },
                borderRadius: '1rem',
                paddingTop: '2rem',
                gap: '1rem',
              },
        },
      }}
      sx={
        isMobile
          ? {
              '& .MuiDialog-container': {
                alignItems: 'flex-end',
              },
            }
          : undefined
      }
    >
      <DialogTitle sx={{ padding: 0 }}>
        <Box sx={{ height: '40px', display: 'flex', alignItems: 'center', position: 'relative' }}>
          {contentReady && image?.source ? (
            <Image
              src={image.source}
              alt={image.alt || displayName || ''}
              fill
              style={{ objectFit: 'contain', objectPosition: 'center' }}
              unoptimized
            />
          ) : null}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: 'neutral.darkest',
              fontSize: {
                md: '1.5rem',
              },
              position: {
                sm: 'absolute',
              },
              left: {
                xs: '88%',
                sm: '90%',
              },
              top: '50%',
              transform: {
                xs: 'translateY(-75%)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      {contentReady ? (
        <DialogContent
          dividers={false}
          sx={{
            display: { xs: 'flex', sm: 'block' },
            flexDirection: { xs: 'column', sm: 'initial' },
            flex: { xs: 'auto', sm: 'initial' },
            minHeight: { xs: 0, sm: 'auto' },
            paddingX: {
              xs: '1.5rem',
              sm: '2rem',
            },
            paddingBottom: {
              xs: 'max(1rem, env(safe-area-inset-bottom, 0px))',
              sm: '2rem',
            },
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Grid
            container
            sx={{
              gap: {
                xs: '1rem',
                sm: '2rem',
              },
              alignItems: 'center',
              flex: { xs: '1 1 auto', sm: 'initial' },
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              minHeight: { xs: 0, sm: 'auto' },
              width: '100%',
            }}
          >
            <Grid size={12}>
              <Typography>
                {definitiveArticle} {displayName} offers a <strong>{apyDescriptor}</strong>{' '}
                {content} <strong>{monthlyFeeDescriptor}</strong>
                {extraContent && ' '}
                {extraContent}.
              </Typography>
            </Grid>

            <Grid
              container
              size={12}
              sx={{
                gap: '0.5rem',
              }}
            >
              <Typography
                variant="textSmall"
                sx={{
                  fontWeight: 325,
                  lineHeight: '150%',
                  letterSpacing: '0.07px',
                  fontSize: '14px',
                }}
              >
                Here's what you'll need to open your account:
              </Typography>
              <Grid size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    backgroundColor: 'neutral.white',
                    borderColor: 'neutral.lighter',
                    borderRadius: '16px',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    width: '100%',
                    padding: '1rem',
                  }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      backgroundColor: 'greens.extraLight',
                      borderRadius: '1000px',
                      flexShrink: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      ...iconBoxStyles,
                    }}
                  >
                    <Box
                      component="img"
                      src={`${env.NEXT_PUBLIC_NW_URL}/cdn/banking/icons/address.svg`}
                      alt=""
                      loading="lazy"
                      aria-hidden
                      sx={{
                        color: theme.palette.greens.darkest,
                        height: {
                          xs: 24,
                          sm: 36,
                        },
                        width: {
                          xs: 24,
                          sm: 36,
                        },
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="text"
                      sx={{ fontWeight: 600, lineHeight: '150%', letterSpacing: '0.08px' }}
                    >
                      Home Address
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    backgroundColor: 'neutral.white',
                    borderColor: 'neutral.lighter',
                    borderRadius: '16px',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    width: '100%',
                    padding: '1rem',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: 'greens.extraLight',
                      borderRadius: '1000px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      ...iconBoxStyles,
                    }}
                  >
                    <Box
                      component="img"
                      src={`${env.NEXT_PUBLIC_NW_URL}/cdn/banking/icons/email.svg`}
                      alt="Email icon"
                      loading="lazy"
                      aria-hidden
                      sx={{
                        color: theme.palette.greens.darkest,
                        height: {
                          xs: 24,
                          sm: 36,
                        },
                        width: {
                          xs: 24,
                          sm: 36,
                        },
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="text"
                      sx={{ fontWeight: 600, lineHeight: '150%', letterSpacing: '0.08px' }}
                    >
                      Email Address
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    backgroundColor: 'neutral.white',
                    borderColor: 'neutral.lighter',
                    borderRadius: '16px',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    width: '100%',
                    padding: '1rem',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: 'greens.extraLight',
                      borderRadius: '1000px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      ...iconBoxStyles,
                    }}
                  >
                    <Box
                      component="img"
                      src={`${env.NEXT_PUBLIC_NW_URL}/cdn/banking/icons/phone.svg`}
                      alt="Phone number icon"
                      loading="lazy"
                      aria-hidden
                      sx={{
                        color: theme.palette.greens.darkest,
                        height: {
                          xs: 24,
                          sm: 36,
                        },
                        width: {
                          xs: 24,
                          sm: 36,
                        },
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="text"
                      sx={{ fontWeight: 600, lineHeight: '150%', letterSpacing: '0.08px' }}
                    >
                      Phone number
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    backgroundColor: 'neutral.white',
                    borderColor: 'neutral.lighter',
                    borderRadius: '16px',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    width: '100%',
                    padding: '1rem',
                  }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      backgroundColor: 'greens.extraLight',
                      borderRadius: '1000px',
                      display: 'flex',
                      gap: '10px',
                      justifyContent: 'center',
                      flexShrink: 0,
                      ...iconBoxStyles,
                    }}
                  >
                    <Box
                      component="img"
                      src={`${env.NEXT_PUBLIC_NW_URL}/cdn/banking/icons/idCard.svg`}
                      alt="id card icon"
                      loading="lazy"
                      aria-hidden
                      sx={{
                        color: theme.palette.greens.darkest,
                        height: {
                          xs: 24,
                          sm: 36,
                        },
                        width: {
                          xs: 24,
                          sm: 36,
                        },
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="text"
                      sx={{ fontWeight: 600, lineHeight: '150%', letterSpacing: '0.08px' }}
                    >
                      Taxpayer ID number (SSN)
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 8,
              }}
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '1rem',
                marginLeft: { xs: 0, sm: 'auto' },
                marginRight: { xs: 0, sm: 'auto' },
              }}
            >
              <Typography
                variant="textTiny"
                sx={{
                  fontSize: '11px',
                  fontWeight: 325,
                  lineHeight: '150%',
                  letterSpacing: '0.055px',
                  textAlign: 'center',
                  whiteSpace: { sm: 'nowrap' },
                }}
              >
                Additional information may be required. Accounts subject to approval.
              </Typography>
              <CtaButton
                cta={cta}
                analyticsPayload={analyticsPayload}
                impressionSectionOverride={buttonAnalytics.impressionSection}
                fullWidth
                subtextStylesOverride={{
                  display: 'block',
                  marginX: 0,
                  fontSize: '11px',
                  lineHeight: '17px',
                  paddingTop: '4px',
                }}
                buttonStylesOverride={{
                  fontSize: '14px',
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
      ) : (
        <DialogContent
          dividers={false}
          sx={{ paddingX: { xs: '1.5rem', sm: '2rem' }, paddingBottom: { xs: '1rem', sm: '2rem' } }}
        />
      )}
    </Dialog>
  );
}
